import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api/endpoints';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Database, AlertCircle, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/useToast';
import { validatePassword } from '@/lib/validation/passwordPolicy';

export const SetPassword = () => {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const { toast } = useToast();

  // `token` is read from the URL once on mount (useSearchParams), so the
  // no-token case is derived via a lazy initializer instead of a synchronous
  // setState in an effect; the async validation below still runs in an
  // effect (its setState calls happen inside a .then/.catch callback, which
  // isn't flagged by react-hooks/set-state-in-effect).
  const [status, setStatus] = useState<'checking' | 'valid' | 'invalid'>(() =>
    token ? 'checking' : 'invalid'
  );
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) return;
    let active = true;
    api.auth
      .validateSetupToken(token)
      .then((r) => {
        if (active) setStatus(r.data?.valid ? 'valid' : 'invalid');
      })
      .catch(() => {
        if (active) setStatus('invalid');
      });
    return () => {
      active = false;
    };
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const pErr = validatePassword(password);
    if (pErr) {
      setError(pErr);
      return;
    }
    if (password !== confirm) { // nosemgrep: rules.eslint.detect-possible-timing-attacks, rules.lgpl.nodejs_scan.javascript-crypto-rule-node_timing_attack  // client-side UX match, not a security comparison
      setError('Passwords do not match');
      return;
    }
    setSubmitting(true);
    try {
      await api.auth.setPassword({ token, new_password: password });
      toast({ title: 'Password set', description: 'You can now sign in with your new password.' });
      navigate('/login');
    } catch (err: unknown) {
      const e = err as { status?: number; message?: string };
      if (e?.status === 410)
        setError('This setup link has expired. Ask an administrator to resend it.');
      else if (e?.status === 400)
        setError('This setup link is invalid or has already been used.');
      else if (e?.status === 422) setError('Password does not meet the requirements.');
      else setError(e?.message || 'Could not set password. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 p-6">
          <div className="flex items-center justify-center mb-2">
            <Database className="h-12 w-12 text-primary" />
          </div>
          <CardTitle className="text-2xl font-bold text-center">Set your password</CardTitle>
          <CardDescription className="text-center">
            Choose a password to finish setting up your account
          </CardDescription>
        </CardHeader>
        <CardContent className="p-6">
          {status === 'checking' && (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}
          {status === 'invalid' && (
            <Alert variant="destructive" role="alert">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                This setup link is invalid or has expired. Please ask an administrator to resend it.
              </AlertDescription>
            </Alert>
          )}
          {status === 'valid' && (
            <form onSubmit={handleSubmit} className="space-y-6" autoComplete="off">
              <div className="space-y-3">
                <Label htmlFor="new-password">
                  New Password <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="new-password"
                  type="password"
                  value={password}
                  autoComplete="new-password"
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="Min 12 chars, mixed case, numbers, symbols"
                />
              </div>
              <div className="space-y-3">
                <Label htmlFor="confirm-password">
                  Confirm Password <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirm}
                  autoComplete="new-password"
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  placeholder="Re-enter your password"
                />
              </div>
              {error && (
                <Alert variant="destructive" role="alert">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <Button
                type="submit"
                className="w-full"
                disabled={submitting || !password || !confirm}
              >
                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {submitting ? 'Setting password...' : 'Set Password'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
